import React, {useEffect, useState} from 'react';
import _ from "lodash"
import {useDispatch, useSelector} from 'react-redux';
import {enqueueSnackbar} from 'notistack';
import {useNavigate, useParams} from 'react-router-dom';
import {selectStrategyById} from 'redux/strategy/strategySlice';
import {Field, Form, Formik} from 'formik';
import {Button, Card, FormControl, Grid, MenuItem, Switch} from '@mui/material';
import {Select as SelectInput} from 'formik-mui';
import {PageHeader} from "components/molecules/PageHeader/PageHeader";
import Typography from "@mui/material/Typography";
import {Box} from "@mui/system";
import Divider from "@mui/material/Divider";
import {selectSubscriptionByStrategyId, updateSubscription} from "redux/subscription/subscriptionSlice";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import {useConfirmation} from "utils/ConfirmationProvider";
import {routes} from "routes";


const AutoTradeForm = () => {
  const dispatch = useDispatch();
  const confirm = useConfirmation()
  const [formSectionsActive, setFormSectionsActive] = useState({
    autoTradeScaling: false,
  });
  const params = useParams();
  const navigate = useNavigate();
  const strategyId = params.strategyId;
  const strategy = useSelector((state) => selectStrategyById(state, strategyId)) ;
  const subscription = useSelector((state) => selectSubscriptionByStrategyId(state, strategyId))

  useEffect(() => {
    let autoTradeScaling = false;

    if(subscription[0]?.autotrading_data){
      if (!_.isEqual(subscription[0].autotrading_data.autoTradeScaling, defaultInitialValues.autoTradeScaling)){
        autoTradeScaling = true;
      }
    }

    setFormSectionsActive({
      autoTradeScaling: autoTradeScaling
    })
  }, []);

  const handleFormSectionToggle = (section, setFieldValue) => {
    setFormSectionsActive({
      ...formSectionsActive,
      [section]: !formSectionsActive[section]
    })
    setFieldValue(section, defaultInitialValues[section])
  }

  const handleStartAutoTrading = async (values) => {
    if (!subscription[0]?.id) {
      enqueueSnackbar('No subscription found to enable AutoTrade.', {variant: 'error'});
      return;
    }
    
    // Prepare the form values
    if(!formSectionsActive["autoTradeScaling"]){
      values.autoTradeScaling = defaultInitialValues["autoTradeScaling"];
    }
    
    await dispatch(updateSubscription({
      subscriptionId: subscription[0].id, 
      subscriptionData: {
        "autotrading_status": "live",
        "autotrading_data": values
      }
    })).unwrap();
    enqueueSnackbar('AutoTrade has been enabled.', {variant: 'success'});
    navigate(routes.autoTrade);
  }

  const handleStopAutoTrading = async (values, resetForm) => {
    if (!subscription[0]?.id) {
      enqueueSnackbar('No subscription found to disable AutoTrade.', {variant: 'error'});
      return;
    }
    
    confirm({
      title: `Stop AutoTrading`,
      message: <>Are you sure you want to turn off AutoTrading for the strategy <b>{strategy.name}</b>?</>,
      onConfirm: async () => {
        await dispatch(updateSubscription({
          subscriptionId: subscription[0].id, 
          subscriptionData: {
            "autotrading_status": "inactive",
            "autotrading_data": null,
            "autotrading_started": null
          }
        })).unwrap();
        enqueueSnackbar('AutoTrade has been disabled.', {variant: 'success'});
        navigate(routes.autoTrade);
      },
      maxWidth: "md"
    });
  }

  const handleSaveChanges = async (values, resetForm) => {
    if (!subscription[0]?.id) {
      enqueueSnackbar('No subscription found to save changes.', {variant: 'error'});
      return;
    }
    
    if(!formSectionsActive["autoTradeScaling"]){
      values.autoTradeScaling = defaultInitialValues["autoTradeScaling"];
    }

    await dispatch(updateSubscription({subscriptionId: subscription[0].id, subscriptionData: {"autotrading_data": values }})).unwrap();
    enqueueSnackbar('AutoTrade configuration has been saved.', {variant: 'success'});
    navigate(routes.autoTrade);
  }


const defaultInitialValues = {
  autoTradeScaling: "1"
};

const initialValues = subscription[0]?.autotrading_data ? subscription[0].autotrading_data : defaultInitialValues;


  return (
    <>

      <PageHeader text1={"Review AutoTrade "} text2={"settings"} description={strategy.name}/>

      <Grid container spacing={6}>
        <Grid item xs={12} lg={6}>
          <Card sx={{mt: 3, p: 3}}>
            <Formik initialValues={initialValues} onSubmit={handleStartAutoTrading}>
              {({isSubmitting, values, dirty, resetForm, setFieldValue}) => (
                <Form>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="mediumText">AutoTrade Scaling</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>Default</Typography>
                        <Switch color="secondary" checked={formSectionsActive["autoTradeScaling"]} onClick={() => {handleFormSectionToggle("autoTradeScaling", setFieldValue)}}/>
                      <Typography>Modify</Typography>
                    </Stack>
                  </Stack>
                  <Collapse in={formSectionsActive["autoTradeScaling"]} timeout="auto" unmountOnExit>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth margin="normal" size="normal" >
                            <Field component={SelectInput} id="autoTradeScaling" name="autoTradeScaling" label="AutoTrade Scaling">
                              <MenuItem value="1">100%</MenuItem>
                              <MenuItem value="2">200%</MenuItem>
                              <MenuItem value="3">300%</MenuItem>
                              <MenuItem value="4">400%</MenuItem>
                              <MenuItem value="5">500%</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                      </Grid>
                  </Collapse>

                  <Divider sx={{mt: 3, mb:3}}/>

                  <Box display="flex" justifyContent="space-between" alignItems="center" >
                    {subscription[0]?.autotrading_status === 'live' ? <Button sx={{mt: 2}} variant="contained" color="error" onClick={() => handleStopAutoTrading(values, resetForm)}> Stop AutoTrade </Button> : <span></span>}
                  <Typography align="right">
                    {subscription[0]?.autotrading_data && 
                      <Button sx={{mt: 2}} variant="contained" color="secondary" onClick={() => handleSaveChanges(values, resetForm)} disabled={!dirty || isSubmitting}> Save Changes </Button>
                    }
                    &nbsp;
                    {subscription[0]?.autotrading_status === 'inactive' && <Button sx={{mt: 2}} type="submit" variant="contained" color="secondary" disabled={isSubmitting}> Turn On AutoTrade </Button>}
                  </Typography>

                  </Box>
                </Form>
              )}
            </Formik>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{mt: 3}}>
            <Typography variant="h5"></Typography>
            <Typography component="p" sx={{mt: 2}}>
            </Typography>
          </Box>
        </Grid>

      </Grid>
    </>
  )

};

export default AutoTradeForm;
